import { Link } from 'react-router-dom';
import { useState } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/uj88omcr_ChatGPT%20Image%20Feb%2011%2C%202026%2C%2009_23_14%20PM.png';
const GUAP_CHARACTER = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

/**
 * GuapGoals Landing Page - Optimized for Conversion & SEO
 * 
 * STRUCTURE:
 * 1. Hero - Clear headline, subheadline, single CTA, trust signals
 * 2. Pain Point - Emotional connection to money stress
 * 3. Solution - How GuapGoals works (3 steps)
 * 4. Features - Gamification explained clearly THEN branded
 * 5. Social Proof - Stats, testimonials
 * 6. Final CTA - Strong close
 * 
 * SEO: All critical content is static HTML, not JS-dependent
 * Mobile-first: All styles optimized for mobile viewing
 */

function LandingPage() {
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <div className="min-h-screen bg-guap-bg text-white">
      {/* Subtle background texture */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none z-0" 
           style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} 
           aria-hidden="true" />

      {/* ========== NAVIGATION ========== */}
      <nav className="relative z-10 border-b border-white/10 bg-guap-bg/95 backdrop-blur-sm sticky top-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="GuapGoals" className="h-10 md:h-12 w-auto" loading="eager" />
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <Link
                to="/app/login"
                className="font-space font-medium text-guap-muted hover:text-white transition-colors px-3 py-2 text-sm md:text-base"
              >
                Login
              </Link>
              <Link
                to="/app/signup"
                data-testid="nav-cta-btn"
                className="bg-guap-green text-black font-bold uppercase text-sm md:text-base px-4 md:px-6 py-2.5 md:py-3 hover:shadow-[3px_3px_0px_0px_#9D00FF] transition-all"
              >
                Start Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ========== HERO SECTION ========== */}
      <section className="relative z-10 pt-12 pb-16 md:pt-20 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            
            {/* Social Proof Badge */}
            <div className="inline-flex items-center gap-2 bg-guap-surface/50 border border-guap-green/30 px-4 py-2 mb-6 rounded-full">
              <span className="text-guap-green text-sm">⭐</span>
              <span className="text-white/80 font-space text-sm">10,000+ users stacking smarter</span>
            </div>
            
            {/* Main Headline - Clear, Outcome-Focused */}
            <h1 className="font-anton text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] mb-6">
              <span className="text-white">Stop Being</span>
              <br />
              <span className="text-guap-green">Broke</span>
              <br />
              <span className="text-white">Start Stacking</span>
            </h1>
            
            {/* Subheadline - Explains what the app does */}
            <p className="font-space text-lg md:text-xl text-white/70 mb-8 max-w-xl mx-auto leading-relaxed">
              The <strong className="text-white">gamified budgeting app</strong> that makes saving money addictive. 
              Track spending, crush debt, and level up your financial status.
            </p>
            
            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/app/signup"
                data-testid="hero-cta-btn"
                className="bg-guap-green text-black font-anton uppercase text-xl px-10 py-4 text-center hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_#9D00FF] transition-all duration-200"
              >
                Start Stacking Free →
              </Link>
              <button
                onClick={() => setShowHowItWorks(true)}
                className="border-2 border-white/30 text-white/80 font-space font-medium px-8 py-4 text-center hover:border-white hover:text-white transition-all"
              >
                See How It Works
              </button>
            </div>
            
            {/* Trust Signals */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-white/50 font-space">
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-guap-green" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Free Forever Plan
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-guap-green" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Bank-Level Security
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-guap-green" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                No Credit Card Required
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PAIN POINT SECTION ========== */}
      <section className="relative z-10 py-16 md:py-20 bg-guap-surface/30 border-y border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase mb-6">
              Sound Familiar?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
                <span className="text-4xl mb-4 block">😰</span>
                <p className="font-space text-white/80">"Where did my paycheck go? It's only the 15th..."</p>
              </div>
              <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
                <span className="text-4xl mb-4 block">🤷</span>
                <p className="font-space text-white/80">"I make good money but somehow I'm always broke"</p>
              </div>
              <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
                <span className="text-4xl mb-4 block">😤</span>
                <p className="font-space text-white/80">"Budgeting apps are boring and I never stick to them"</p>
              </div>
            </div>
            <p className="font-space text-lg text-guap-green mt-8">
              You're not bad with money. You just need the right system.
            </p>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS (3 STEPS) ========== */}
      <section className="relative z-10 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase mb-4">
              How GuapGoals Works
            </h2>
            <p className="font-space text-white/60 max-w-lg mx-auto">
              Get started in under 2 minutes. No linking bank accounts required.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-guap-green text-black font-anton text-2xl flex items-center justify-center mx-auto mb-4">
                1
              </div>
              <h3 className="font-anton text-xl text-white uppercase mb-2">Add Your Income</h3>
              <p className="font-space text-white/60 text-sm">
                Enter your paychecks and income sources. Takes 30 seconds.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-guap-green text-black font-anton text-2xl flex items-center justify-center mx-auto mb-4">
                2
              </div>
              <h3 className="font-anton text-xl text-white uppercase mb-2">Track Your Bills</h3>
              <p className="font-space text-white/60 text-sm">
                Add recurring bills. We'll show exactly what's left to spend.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-guap-green text-black font-anton text-2xl flex items-center justify-center mx-auto mb-4">
                3
              </div>
              <h3 className="font-anton text-xl text-white uppercase mb-2">Start Stacking</h3>
              <p className="font-space text-white/60 text-sm">
                Build daily streaks, earn ranks, and watch your savings grow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== GAMIFICATION SECTION ========== */}
      <section className="relative z-10 py-16 md:py-24 bg-gradient-to-b from-guap-bg to-guap-surface/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-guap-purple/20 text-guap-purple font-space font-bold text-sm uppercase tracking-wider px-4 py-2 mb-4 rounded-full">
              🎮 The Secret Sauce
            </span>
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase mb-4">
              Budgeting That Actually <span className="text-guap-green">Feels Good</span>
            </h2>
            <p className="font-space text-white/60 max-w-xl mx-auto">
              Turn saving money into a game. Build streaks, level up your financial status, and compete with friends.
            </p>
          </div>
          
          {/* Street Cred System */}
          <div className="max-w-4xl mx-auto mb-12">
            <h3 className="font-anton text-2xl text-center text-guap-purple uppercase mb-8">
              🏆 Street Cred Rank System
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-guap-bg border border-white/10 p-4 text-center rounded-lg">
                <span className="text-3xl block mb-2">📄</span>
                <p className="font-anton text-white text-sm">PAPER CHASER</p>
                <p className="font-space text-white/50 text-xs">7 Day Streak</p>
              </div>
              <div className="bg-guap-bg border border-guap-green/30 p-4 text-center rounded-lg">
                <span className="text-3xl block mb-2">💰</span>
                <p className="font-anton text-guap-green text-sm">BAG GETTER</p>
                <p className="font-space text-white/50 text-xs">30 Day Streak</p>
              </div>
              <div className="bg-guap-bg border border-orange-500/30 p-4 text-center rounded-lg">
                <span className="text-3xl block mb-2">🔥</span>
                <p className="font-anton text-orange-500 text-sm">HEAVY HITTER</p>
                <p className="font-space text-white/50 text-xs">90 Day Streak</p>
              </div>
              <div className="bg-guap-bg border border-guap-purple/30 p-4 text-center rounded-lg">
                <span className="text-3xl block mb-2">👑</span>
                <p className="font-anton text-guap-purple text-sm">MOGUL STATUS</p>
                <p className="font-space text-white/50 text-xs">365 Day Streak</p>
              </div>
            </div>
          </div>
          
          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
              <div className="flex items-start gap-4">
                <span className="text-3xl">🎯</span>
                <div>
                  <h4 className="font-anton text-lg text-white uppercase mb-2">Money Challenges</h4>
                  <p className="font-space text-white/60 text-sm">
                    Compete with friends on "No Eating Out" or "$5/day" challenges. Track progress, share wins.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
              <div className="flex items-start gap-4">
                <span className="text-3xl">💎</span>
                <div>
                  <h4 className="font-anton text-lg text-white uppercase mb-2">Envelope Savings</h4>
                  <p className="font-space text-white/60 text-sm">
                    Pick random amounts to save daily. Gamified saving that adds up fast without the pain.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
              <div className="flex items-start gap-4">
                <span className="text-3xl">☃️</span>
                <div>
                  <h4 className="font-anton text-lg text-white uppercase mb-2">Debt Snowball</h4>
                  <p className="font-space text-white/60 text-sm">
                    Visualize your debt-free date. Choose avalanche or snowball strategy and watch it melt.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-guap-bg/50 border border-white/10 p-6 rounded-lg">
              <div className="flex items-start gap-4">
                <img src={GUAP_CHARACTER} alt="Guapo AI" className="w-10 h-10 rounded-full" />
                <div>
                  <h4 className="font-anton text-lg text-white uppercase mb-2">AI Money Coach</h4>
                  <p className="font-space text-white/60 text-sm">
                    Meet Guapo. Get personalized spending insights and tips that speak your language.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SOCIAL PROOF ========== */}
      <section className="relative z-10 py-16 md:py-20 bg-guap-surface/30 border-y border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase mb-4">
              Real Results
            </h2>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            <div className="text-center">
              <p className="font-anton text-4xl md:text-5xl text-guap-green">$2.5M+</p>
              <p className="font-space text-white/60 text-sm">Debt Tracked</p>
            </div>
            <div className="text-center">
              <p className="font-anton text-4xl md:text-5xl text-guap-green">10K+</p>
              <p className="font-space text-white/60 text-sm">Active Users</p>
            </div>
            <div className="text-center">
              <p className="font-anton text-4xl md:text-5xl text-guap-green">47</p>
              <p className="font-space text-white/60 text-sm">Avg Streak Days</p>
            </div>
            <div className="text-center">
              <p className="font-anton text-4xl md:text-5xl text-guap-green">4.9★</p>
              <p className="font-space text-white/60 text-sm">User Rating</p>
            </div>
          </div>
          
          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-guap-bg border border-white/10 p-6 rounded-lg">
              <p className="font-space text-white/80 text-sm mb-4">
                "First budgeting app I've actually stuck with. The streak system is addictive in the best way."
              </p>
              <p className="font-space text-guap-green text-sm font-bold">— Marcus, 23</p>
              <p className="font-space text-white/50 text-xs">67 day streak</p>
            </div>
            <div className="bg-guap-bg border border-white/10 p-6 rounded-lg">
              <p className="font-space text-white/80 text-sm mb-4">
                "Paid off $4,200 in credit card debt using the snowball feature. Actually seeing my debt-free date kept me motivated."
              </p>
              <p className="font-space text-guap-green text-sm font-bold">— Jasmine, 28</p>
              <p className="font-space text-white/50 text-xs">Heavy Hitter rank</p>
            </div>
            <div className="bg-guap-bg border border-white/10 p-6 rounded-lg">
              <p className="font-space text-white/80 text-sm mb-4">
                "My friend group does money challenges together now. We saved $800 combined last month just competing."
              </p>
              <p className="font-space text-guap-green text-sm font-bold">— Devon, 25</p>
              <p className="font-space text-white/50 text-xs">Challenge winner 3x</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="relative z-10 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase mb-6">
              Ready to <span className="text-guap-green">Level Up?</span>
            </h2>
            <p className="font-space text-lg text-white/70 mb-8">
              Join thousands building real financial freedom. Free forever. Start in 60 seconds.
            </p>
            <Link
              to="/app/signup"
              data-testid="final-cta-btn"
              className="inline-block bg-guap-green text-black font-anton uppercase text-2xl px-12 py-5 hover:-translate-y-1 hover:shadow-[8px_8px_0px_0px_#9D00FF] transition-all duration-200"
            >
              Start Stacking Free →
            </Link>
            <p className="font-space text-white/40 text-sm mt-6">
              No credit card required • Free plan available forever
            </p>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="relative z-10 py-8 border-t border-white/10 bg-guap-surface/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="GuapGoals" className="h-8 w-auto" />
              <span className="font-space text-white/50 text-sm">© 2026 GuapGoals</span>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="font-space text-white/50 text-sm hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="font-space text-white/50 text-sm hover:text-white transition-colors">
                Terms
              </Link>
              <a 
                href="https://forms.gle/d895nsCtERWW1pDt9" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-space text-white/50 text-sm hover:text-white transition-colors"
              >
                Feedback
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* ========== HOW IT WORKS MODAL ========== */}
      {showHowItWorks && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowHowItWorks(false)}
        >
          <div 
            className="bg-guap-bg border-2 border-guap-green max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-anton text-3xl text-white uppercase">How It Works</h2>
                  <p className="font-space text-white/60 mt-1">Start in under 2 minutes</p>
                </div>
                <button 
                  onClick={() => setShowHowItWorks(false)}
                  className="text-white/60 hover:text-white text-2xl p-2"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-guap-green text-black font-anton text-xl flex items-center justify-center shrink-0">1</div>
                  <div>
                    <h3 className="font-anton text-lg text-white mb-1">CREATE YOUR FREE ACCOUNT</h3>
                    <p className="font-space text-white/60 text-sm">Sign up in seconds. No credit card required.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-guap-green text-black font-anton text-xl flex items-center justify-center shrink-0">2</div>
                  <div>
                    <h3 className="font-anton text-lg text-white mb-1">SET UP INCOME & BILLS</h3>
                    <p className="font-space text-white/60 text-sm">Add your pay dates and recurring expenses.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-guap-green text-black font-anton text-xl flex items-center justify-center shrink-0">3</div>
                  <div>
                    <h3 className="font-anton text-lg text-white mb-1">BUILD YOUR STREAK</h3>
                    <p className="font-space text-white/60 text-sm">Log daily spending, earn ranks, level up your financial status.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-guap-green text-black font-anton text-xl flex items-center justify-center shrink-0">4</div>
                  <div>
                    <h3 className="font-anton text-lg text-white mb-1">CRUSH YOUR GOALS</h3>
                    <p className="font-space text-white/60 text-sm">Use challenges, debt snowball, and envelope savings to stack.</p>
                  </div>
                </div>
              </div>
              
              <Link
                to="/app/signup"
                className="block w-full bg-guap-green text-black font-anton uppercase text-xl py-4 text-center hover:shadow-[4px_4px_0px_0px_#9D00FF] transition-all"
                onClick={() => setShowHowItWorks(false)}
              >
                Get Started Free →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;
